import {useEffect} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as api from "../../api";
import {useTranslation} from "react-i18next";
import {useFeedbackTools} from "../../components/FeedbackTools";
import {useNavigate} from "react-router-dom";


export const useModels1Query = (models1Filters={}) => {
    const query = useQuery({
        queryKey: ['models1', models1Filters],
        queryFn: () => api.Model1.getModels1(models1Filters),
        keepPreviousData: true
    });

    // Prefetch the next page!
    const queryClient = useQueryClient()
    const {isPreviousData} = query;
    const hasMoreData = query.data?.page_count > models1Filters.pageIndex + 1;

    // note that models1Filters should be memorized to avoid useless prefetch
    useEffect(() => {
        if (!isPreviousData && hasMoreData) {
            const nextPageFilter = {...models1Filters, pageIndex: models1Filters.pageIndex + 1}
            queryClient.prefetchQuery({
                queryKey: ['models1', nextPageFilter],
                queryFn: () => api.Model1.getModels1(nextPageFilter),
            })
        }
    }, [isPreviousData, hasMoreData, models1Filters, queryClient])

    return query;
}

export const useModel1RegistryQuery = () => {
    const {i18n} = useTranslation();
    const locale = i18n.language;
    return useQuery({
        queryKey: ['registry', 'model1', locale],
        queryFn: () => api.Registry.getModel1Registry(locale),
        keepPreviousData: true,
        staleTime: 1000 * 60 * 60 * 24 * 7, // 1 week
    });
}


export const useModel1ValidationRegistryQuery = () => {
    const {i18n} = useTranslation();
    const locale = i18n.language;
    return useQuery({
        queryKey: ['registry', 'model1Validation', locale],
        queryFn: () => api.Registry.getModel1ValidationRegistry(locale),
        keepPreviousData: true,
        staleTime: 1000 * 60 * 60 * 24 * 7, // 1 week
    });
}


export const useBulkUpdateModels1 = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data) => await api.Model1.bulkUpdateModels1(data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['models1']);
        }
    });
}

export const useCreateModel1 = () => {
    const {t} = useTranslation(["models1"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.Model1.createModel1(data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['models1']);
            queryClient.invalidateQueries(['geo', 'model1Stations']);
            showSuccessNotification(t('query.createSuccess'));
        }
    });
}

export const useUpdateModel1 = (id) => {
    const {t} = useTranslation(["models1"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.Model1.updateModel1(id, data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['models1']);
            queryClient.invalidateQueries(['geo', 'model1Stations']);
            showSuccessNotification(t('query.updateSuccess'));
        }
    });
}


export const useDeleteModel1 = () => {
    const {t} = useTranslation(["models1"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async ({id, showNotification = true}) => {
            const res = await api.Model1.deleteModel1(id);
            return {res, showNotification};
        },
        onSuccess: async (data) => {
            const {showNotification} = data;
            if (showNotification) {
                await navigate('/models1')
                showSuccessNotification(t('query.deleteSuccess'));
            }
            queryClient.invalidateQueries(['models1']);
            queryClient.invalidateQueries(['geo', 'model1Stations']);
        }
    });
}


export const useModel1FailedValidationsMutation = () => {
    return useMutation({
        mutationFn: async (data) => await api.Model1.getFailedValidations(data),
    });
}


export const useModel1Query = (id, props={}) => {
    return useQuery({
        queryKey: ['models1', id],
        queryFn: () => api.Model1.getModel1(id),
        keepPreviousData: true,
        ...props
    });
}

export const useModel1ExportMutation = () => {
    return useMutation({
        mutationFn: async (data) => await api.Model1.exportModels1(data),
        onSuccess: (data, ) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([data.data]));
            link.download = data.fileName; // specify the filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    });
}

export const useCreateModel1ExportTask = () => {
    return useMutation({
        mutationFn: async ({workgroupId, filters, data}) => await api.ExportTask.createModel1ExportTask(workgroupId, filters, data),
    });
}